import React, { useCallback, useEffect } from 'react';
import { useCanvasPopup } from '../../services/editor/CanvasPopup';
import CanvasPopupBaseComponent from '../popup/CanvasPopupBaseComponent';
import { RelativeObjectsState } from '../canvas';
import { IDropdownMenuCardOption } from './DropdownMenuCard';
import { EditorService } from '../../services/editor';

export interface ICanvasDropdownMenuCardOption<Value = string>
	extends IDropdownMenuCardOption<Value> {
	id: string;
	parentCanvasObject: RelativeObjectsState;
	project: EditorService;
}

export default function CanvasDropdownMenuCardOption<Value = string>(
	props: ICanvasDropdownMenuCardOption<Value>
) {
	const memoizedCanvasPopupBody = useCallback(
		() => (
			<CanvasPopupBaseComponent containerClasses={['tooltip', 'tooltip--large-font']}>
				{props.tooltip}
			</CanvasPopupBaseComponent>
		),
		[props.tooltip]
	);

	const canvasPopup = useCanvasPopup(
		{
			editor: props.project,
			id: props.id,
			arrowClasses: ['tooltip-arrow'],
			anchorQuerySelector: '#dropdown-menu-card-option--' + props.id,
			parentCanvasObject: props.parentCanvasObject,
		},
		memoizedCanvasPopupBody,
	)
		.setupHover({
			closeDelay: 80,
			openDelay: 600,
		})
		.onBeforeShow(() => {
			// Only show if the 'tooltip' value is set
			return !!props.tooltip;
		});

	// useEffect(() => {
	// 	return () => {
	// 		canvasPopup.destroy();
	// 	};
	// }, [canvasPopup]);

	const {
		label,
		iconEnd,
		iconStart,
		color = 'primary',
		disabled = false,
		navigateTo,
		value,
		child,
		onClick,
		id,
		style,
		classes,
	} = props;
	const selected = value === props.value && !!value;

	return (
		<span>
			{child ? (
				child
			) : navigateTo ? (
				<a
					style={style}
					ref={canvasPopup.anchorRef}
					href={navigateTo}
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						e.nativeEvent.stopImmediatePropagation();
						if (!disabled) {
							EditorService.navigate(navigateTo);
						}
					}}
					id={'dropdown-menu-card-option--' + id}
					className={[
						'dropdown-menu-card-option',
						'dropdown-menu-card-option__color-' + color,
						disabled ? 'dropdown-menu-card-option__disabled' : '',
						selected && !disabled ? 'dropdown-menu-card-option__selected' : '',
						...(classes || []),
					].join(' ')}
				>
					{iconStart && <i className={iconStart} />}
					{label}
					{(iconEnd || (!iconEnd && !iconStart)) && (
						<i
							className={[
								'fa-solid',
								iconEnd ? 'fa-' + iconEnd : 'fa-arrow-right',
							].join(' ')}
						/>
					)}
				</a>
			) : (
				<button
					style={style}
					ref={canvasPopup.anchorRef}
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						e.nativeEvent.stopImmediatePropagation();
						onClick?.(value, e);
					}}
					id={'dropdown-menu-card-option--' + id}
					className={[
						'dropdown-menu-card-option',
						'dropdown-menu-card-option__color-' + color,
						disabled ? 'dropdown-menu-card-option__disabled' : '',
						selected && !disabled ? 'dropdown-menu-card-option__selected' : '',
						...(classes || []),
					].join(' ')}
					disabled={disabled}
				>
					<span style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
						{iconStart && <i className={iconStart} />}
						{label}
					</span>
					{(iconEnd || (!iconEnd && !iconStart)) && (
						<i className={iconEnd || 'fa-solid fa-arrow-right'} />
					)}
				</button>
			)}
		</span>
	);
}
